import {useMemo} from 'react'
import {useQuery} from 'react-query'

import {api} from '../../App.global'
import {BusinessLineType} from '../../common/types'
import {useFeaturesState} from '../../Organisms/Features'
import {
  ORDER_CREATE_API_PATH,
  ORDER_REQUESTS_API_PATH,
  V2_ORDER_REQUESTS_API_PATH
} from '../../Organisms/OrderIntake/OrderIntake.action'
import {ShippingType} from '../declarations/OrderIntake.enums'
import {
  OptimizedOption,
  OptionsV2,
  OrderIntakeMaterialOptionPayload,
  OrderIntakeOptions
} from '../declarations/types'
import {useOrderIntakeData} from '../hooks'

import {mapOptimizedOptionsToLegacy, mapV2OptionsToLegacy} from './mapper'
import * as options from './options'
import {ErrorCodesForOption} from './options'

export const OptionsKeys = {
  all: () => ['OrderRequests/options'] as const,
  byUserId: (customerId?: string) => [...OptionsKeys.all(), customerId] as const
}

export const MultiLoadOptionsKeys = {
  all: () => ['OrderRequests/multiload-options'] as const,
  byUserId: (customerId?: string) => [...MultiLoadOptionsKeys.all(), customerId] as const
}

export const V2OptionsKeys = {
  all: (enableOrderCreateApi: boolean) =>
    [enableOrderCreateApi ? 'order-create/options' : 'v2/OrderRequests/options'] as const,
  byUserIdAndBusinessLine: (
    enableOrderCreateApi: boolean,
    customerId?: string,
    businessLine?: string
  ) => [...V2OptionsKeys.all(enableOrderCreateApi), customerId, businessLine] as const
}

export const useV2OptionsBase = (
  applyMaterialEnteredDescription: boolean,
  applyContractItemDescription: boolean,
  customerId?: string
) => {
  const {businessLine} = useOrderIntakeData()!
  const {getFeature} = useFeaturesState()
  const enableV2Options = getFeature('EnableV2Options')!
  const enableOrderCreateApi = !!getFeature('EnableOrderCreateApi')
  const isRMCBusinessLine = businessLine === BusinessLineType.RMC
  const v2ApiPath = enableOrderCreateApi ? ORDER_CREATE_API_PATH : V2_ORDER_REQUESTS_API_PATH

  return useQuery(
    V2OptionsKeys.byUserIdAndBusinessLine(enableOrderCreateApi, customerId, businessLine),
    async ({queryKey}) => {
      const response = await api.get(`${v2ApiPath}/options`, {
        params: {customerId: queryKey[1], businessLine: businessLine}
      })

      return mapV2OptionsToLegacy(
        response.data as OptionsV2,
        applyMaterialEnteredDescription,
        applyContractItemDescription
      )
    },
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      enabled: !!customerId && !!businessLine && enableV2Options && !isRMCBusinessLine
    }
  )
}

export const useOptionsBase = (
  applyMaterialEnteredDescription: boolean,
  applyContractItemDescription: boolean,
  customerId?: string
) => {
  const {getFeature} = useFeaturesState()
  const enableV2Options = getFeature('EnableV2Options')!

  return useQuery(
    OptionsKeys.byUserId(customerId),
    ({queryKey}) => {
      return api
        .get<OptimizedOption[]>(`${ORDER_REQUESTS_API_PATH}/options`, {
          params: {customerId: queryKey[1]}
        })
        .then((d) =>
          mapOptimizedOptionsToLegacy(
            d.data,
            applyMaterialEnteredDescription,
            applyContractItemDescription,
            queryKey[1]
          )
        )
    },
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      enabled: !!customerId && !enableV2Options
    }
  )
}

export const useMultiLoadOptionsBase = (
  applyMaterialEnteredDescription: boolean,
  applyContractItemDescription: boolean,
  customerId?: string
) => {
  return useQuery(
    MultiLoadOptionsKeys.byUserId(customerId),
    ({queryKey}) => {
      return api
        .get<OptimizedOption[]>(`${ORDER_REQUESTS_API_PATH}/options`, {
          params: {customerId: queryKey[1], isMultiLoadCall: true}
        })
        .then((d) =>
          mapOptimizedOptionsToLegacy(
            d.data,
            applyMaterialEnteredDescription,
            applyContractItemDescription,
            queryKey[1]
          )
        )
    },
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      enabled: !!customerId
    }
  )
}

const byShippingTypePredicate = (t: ShippingType) => (s: OrderIntakeMaterialOptionPayload) =>
  s.shippingType === t

const shippingTypeFactory = (t?: ShippingType) => {
  const shippingTypePredicate = t ? byShippingTypePredicate(t) : () => true
  return (configuration?: options.Configuration, customerId?: string) => {
    const {getFeature} = useFeaturesState()
    const enableV2Options = getFeature('EnableV2Options')!

    const v1OptionBase = useOptionsBase(
      configuration?.applyMaterialEnteredDescription ?? false,
      configuration?.applyContractItemDescription ?? false,
      customerId
    )

    const v2OptionBase = useV2OptionsBase(
      configuration?.applyMaterialEnteredDescription ?? false,
      configuration?.applyContractItemDescription ?? false,
      customerId
    )

    const {data, ...rest} = enableV2Options ? v2OptionBase : v1OptionBase

    const result = useMemo(
      () => (data ? options.fromOptionPayload(data?.filter(shippingTypePredicate)) : undefined),
      [data]
    )
    return {
      ...rest,
      data: result
    }
  }
}

export const useCollectionOptions = shippingTypeFactory(ShippingType.COLLECT)
export const useDeliveryOptions = shippingTypeFactory(ShippingType.DELIVER)
export const useDefaultOptions = shippingTypeFactory()

export const useMultiLoadDeliveryOptions = (
  configuration?: options.Configuration,
  customerId?: string
) => {
  const multiLoadOptionBase = useMultiLoadOptionsBase(
    configuration?.applyMaterialEnteredDescription ?? false,
    configuration?.applyContractItemDescription ?? false,
    customerId
  )

  const {data, ...rest} = multiLoadOptionBase

  const result = useMemo(
    () =>
      data
        ? options.fromOptionPayload(data?.filter(byShippingTypePredicate(ShippingType.DELIVER)))
        : undefined,
    [data]
  )

  return {
    ...rest,
    data: result
  }
}

export type InvalidOrderIntakeOptions = {
  errorCode: ErrorCodesForOption
}
export const isInvalidOrderIntakeOption = function (o: unknown): o is InvalidOrderIntakeOptions {
  if (typeof o !== 'object' && o !== null) return false
  return typeof (o as unknown as ErrorCodesForOption)['errorCode'] === 'string'
}

const getDefaultOrderRequest = (
  shippingType: ShippingType,
  data?: OrderIntakeOptions,
  configuration?: options.Configuration,
  siteNumber?: string
) => {
  try {
    return data
      ? options.createDefaultOrderRequest(shippingType, data, configuration, siteNumber)
      : undefined
  } catch (e: unknown) {
    const message = (e as Error)?.message as ErrorCodesForOption
    if (!message) return
    return {errorCode: message} as InvalidOrderIntakeOptions
  }
}
export const useDefaultCollection = (
  customerId?: string,
  configuration?: options.Configuration,
  siteNumber?: string
) => {
  const {data, ...rest} = useCollectionOptions(configuration, customerId)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const result = useMemo(
    () => getDefaultOrderRequest(ShippingType.COLLECT, data, configuration, siteNumber),
    [data, siteNumber]
  )
  return {...rest, data: result}
}
export const useDefaultDelivery = (
  customerId?: string,
  configuration?: options.Configuration,
  siteNumber?: string
) => {
  const {data, ...rest} = useDeliveryOptions(configuration, customerId)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const result = useMemo(
    () => getDefaultOrderRequest(ShippingType.DELIVER, data, configuration, siteNumber),
    [data, siteNumber]
  )
  return {...rest, data: result}
}

export const useDefaultBusinessLine = (
  customerId?: string,
  configuration?: options.Configuration
) => {
  const {data, ...rest} = useDefaultOptions(configuration, customerId)
  // There are obsolete customers which contain no options, we need to handle those
  let businessLine: BusinessLineType | undefined
  if (data && Object.values(data).length > 0) {
    businessLine = options.getBusinessLine(data)
  }

  return {...rest, data: businessLine}
}
